import axios, { AxiosRequestConfig } from 'axios';
import countries from 'i18n-iso-countries';

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'Content-Type': 'application/json',
    countryCode: (localStorage.getItem('countryCode') || process.env.REACT_APP_COUNTRYCODE) as string,
    languageCode: localStorage.getItem('languageCode') || (process.env.REACT_APP_LANGUAGECODE) as string,
  },
});

const applyIrelandOverride = (req: AxiosRequestConfig) => {
  try {
    const isIreland = localStorage.getItem('isIreland') === 'true';
    const existingCountryCode = req.headers?.countryCode || localStorage.getItem('countryCode') || process.env.REACT_APP_COUNTRYCODE;

    // If we previously detected Ireland and the header is still GBR, force IRL
    if (isIreland && existingCountryCode === 'GBR') {
      req.headers = req.headers || {};
      req.headers.countryCode = 'IRL';
    }
    // If params specify Ireland (countryIds includes 102) also force IRL
    if (req.params && typeof req.params === 'object') {
      const countryIds = (req.params as any).countryIds;
      if (countryIds && countryIds.toString().split(',').includes('102')) {
        req.headers = req.headers || {};
        req.headers.countryCode = 'IRL';
      }
    }
  } catch (e) {
    // Silent fail – do not block the request
  }
};

const reqInterceptor = async (req: AxiosRequestConfig) => {
  if (req.headers?.externalName === null) {
    delete req.headers?.externalName;
  }
  if (req.headers && !req.headers?.externalName) {
    //  FIXME: Quick workaround
    const url = new URL(window.location.toString());
    if (url.searchParams.get('variant') === 'ford') {
      req.headers['externalName'] = 'ford';
    }
  }

  // Ireland override logic(since UK and wheretobuy are sharing same wheretobuy instance)
  applyIrelandOverride(req);

  req.headers = {
    ...req.headers,
  }
  return req;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const msdsPdsInterceptor = (res: any) => {
  const URL_MATCH_REGEX = /(recommendations|products)(\/?\d+|\??vehicleId=*)/;
  const {
    config: {
      url,
      headers: { countryCode },
    },
  } = res;
  const countryAlpha2 = countries.alpha3ToAlpha2(countryCode);
  const regionNames = new Intl.DisplayNames(["en"], { type: "region" });
  const countryName = regionNames.of(countryAlpha2 ?? '');

  if (
    ["MYS", "DEU", "AUT"].includes(countryCode) &&
    URL_MATCH_REGEX.test(url)
  ) {
    const match = res.config.url.match(URL_MATCH_REGEX);
    const countryMsdsName = `${countryName} (${countryAlpha2})`;

    if ([match.at(1), match.at(2)].includes("products")) {
      const { id } = res.data;
      if (id && id !== 99999 && id !== 77777) {
        const msdsUrl = 'msdsUrl' in res.data && res.data.msdsUrl != null ? new URL(res.data.msdsUrl) : null;
        const pdsUrl = 'pdsUrl' in res.data && res.data.pdsUrl ? new URL(res.data.pdsUrl) : null;
        ["l", "p"].forEach((item: any) => {
          if (msdsUrl) {
            msdsUrl.searchParams.delete(item);
          }
          if (pdsUrl) {
            pdsUrl.searchParams.delete(item);
          }
        });
        if (msdsUrl) {
          msdsUrl.searchParams.set("c", countryMsdsName);
          msdsUrl.searchParams.set("n", res.data.id);
          res.data.msdsUrl = msdsUrl;
        }
        else {
          res.data.msdsUrl = null;
        }
        if (pdsUrl) {
          pdsUrl.searchParams.set("c", countryMsdsName);
          pdsUrl.searchParams.set("n", res.data.id);
          res.data.pdsUrl = pdsUrl;
        }
      }
    } else {
      res.data.components.forEach((x: any) =>
        processComponentMsdsPds(x, countryMsdsName)
      );
    }
  }

  return res;
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const processComponentMsdsPds = (component: any, countryMsdsName: string) => {
  if ("recommendedProduct" in component) {
    if ("msdsUrl" in component.recommendedProduct && component.recommendedProduct.msdsUrl) {
      const { id } = component.recommendedProduct;
      if (id && id !== 99999 && id !== 77777) {
        const msdsUrl = new URL(component.recommendedProduct.msdsUrl);
        msdsUrl.searchParams.delete("l");
        msdsUrl.searchParams.delete("p");
        msdsUrl.searchParams.set("c", countryMsdsName);
        msdsUrl.searchParams.set("n", id);
        component.recommendedProduct.msdsUrl = msdsUrl.toString();
      }
    }
    if ("pdsUrl" in component.recommendedProduct && component.recommendedProduct.pdsUrl) {
      const { id } = component.recommendedProduct;
      if (id && id !== 99999 && id !== 77777) {
        const pdsUrl = new URL(component.recommendedProduct.pdsUrl);
        pdsUrl.searchParams.delete("l");
        pdsUrl.searchParams.delete("p");
        pdsUrl.searchParams.set("c", countryMsdsName);
        pdsUrl.searchParams.set("n", id);
        component.recommendedProduct.pdsUrl = pdsUrl.toString();
      }
    }
  }
  if ("alternateProducts" in component) {
    component.alternateProducts.forEach((product: any) => {
      if ("msdsUrl" in product && product.msdsUrl) {
        const { id } = product;
        if (id && id !== 99999 && id !== 77777) {
          const msdsUrl = new URL(component.recommendedProduct.msdsUrl);
          msdsUrl.searchParams.delete("l");
          msdsUrl.searchParams.delete("p");
          msdsUrl.searchParams.set("c", countryMsdsName);
          msdsUrl.searchParams.set("n", id);
          product.msdsUrl = msdsUrl.toString();
        }
      }
      if ("pdsUrl" in product && product.pdsUrl) {
        const { id } = product;
        if (id && id !== 99999 && id !== 77777) {
          const pdsUrl = new URL(component.recommendedProduct.pdsUrl);
          pdsUrl.searchParams.delete("l");
          pdsUrl.searchParams.delete("p");
          pdsUrl.searchParams.set("c", countryMsdsName);
          pdsUrl.searchParams.set("n", id);
          product.pdsUrl = pdsUrl.toString();
        }
      }
    });
  }
}

axiosInstance.interceptors.response.use(msdsPdsInterceptor, function (err) {
  return Promise.resolve(err === null || err === void 0 ? void 0 : err.response);
});

axiosInstance.interceptors.request.use(reqInterceptor, error => Promise.reject(error));

export const apiInstance = axiosInstance;
